import './styles.css';
import { demoProfile, demoReviews, marketSignals, pricingPlans } from './data';
import {
  aggregateMetrics,
  buildDraftResponse,
  classifyReview,
  DEFAULT_PROFILE,
  needsHumanReview,
  parseCsvReviews,
  PLAN_LIMITS,
  sanitizeReviewText,
  serializeReport,
  type BusinessProfile,
  type BrandVoice,
  type Review,
  type ReviewStatus
} from './domain';

const STORAGE_KEY = 'reviewreply.mvp.state.v1';

type Tab = 'inbox' | 'reports' | 'billing' | 'setup';

interface AppState {
  profile: BusinessProfile;
  reviews: Review[];
  selectedReviewId: string | null;
  activeTab: Tab;
  eventLog: Array<{ at: string; event: string; detail: string }>;
  toast: string;
}

const defaultState: AppState = {
  profile: demoProfile,
  reviews: createDraftedReviews(demoReviews, demoProfile),
  selectedReviewId: demoReviews[0]?.id ?? null,
  activeTab: 'inbox',
  eventLog: [{ at: new Date().toISOString(), event: 'demo_loaded', detail: 'Demo account initialized with five reviews.' }],
  toast: ''
};

let state = loadState();

function initialReviewStatus(review: Review): ReviewStatus {
  return needsHumanReview(review) ? 'needs_review' : 'drafted';
}

function createDraftedReviews(reviews: Review[], profile: BusinessProfile): Review[] {
  return reviews.map((review): Review => ({
    ...review,
    responseDraft: buildDraftResponse(review, profile),
    status: initialReviewStatus(review)
  }));
}

function loadState(): AppState {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (!stored) return defaultState;
  try {
    const parsed = JSON.parse(stored) as AppState;
    return {
      ...defaultState,
      ...parsed,
      profile: { ...DEFAULT_PROFILE, ...parsed.profile },
      reviews: parsed.reviews?.length ? parsed.reviews : defaultState.reviews,
      eventLog: parsed.eventLog?.length ? parsed.eventLog : defaultState.eventLog
    };
  } catch {
    return defaultState;
  }
}

function saveState(): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, toast: '' }));
}

function track(event: string, detail: string): void {
  state.eventLog = [{ at: new Date().toISOString(), event, detail }, ...state.eventLog].slice(0, 20);
  saveState();
}

function setState(next: Partial<AppState>, shouldTrack?: { event: string; detail: string }): void {
  state = { ...state, ...next };
  if (shouldTrack) track(shouldTrack.event, shouldTrack.detail);
  saveState();
  render();
}

function updateReview(id: string, patch: Partial<Review>, event?: string): void {
  const reviews = state.reviews.map((review) => (review.id === id ? { ...review, ...patch } : review));
  const updated = reviews.find((review) => review.id === id);
  state = { ...state, reviews };
  if (event && updated) track(event, `${updated.source} review from ${updated.reviewerName}`);
  saveState();
  render();
}

function selectedReview(): Review | undefined {
  return state.reviews.find((review) => review.id === state.selectedReviewId) ?? state.reviews[0];
}

function statusLabel(status: ReviewStatus): string {
  return status.replace('_', ' ');
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

function money(value: number): string {
  return new Intl.NumberFormat('en', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
}

function pct(value: number): string {
  return `${Math.round(value)}%`;
}

function riskClass(review: Review): string {
  return classifyReview(review).risk;
}

function escapeHtml(value: string | number): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function appShell(content: string): string {
  const metrics = aggregateMetrics(state.reviews, state.profile);
  const plan = PLAN_LIMITS[state.profile.plan];
  return `
    <header class="hero">
      <nav class="topbar" aria-label="Primary navigation">
        <div class="brand"><span class="brand-mark">RR</span><span>ReviewReply</span></div>
        <div class="nav-actions">
          <button class="ghost" data-action="reset-demo">Reset demo</button>
          <button class="primary" data-tab="billing">Upgrade from ${money(plan.price)}/mo</button>
        </div>
      </nav>
      <section class="hero-grid">
        <div>
          <p class="eyebrow">Review response operations for local teams</p>
          <h1>Turn review chaos into approved replies, risk alerts and ROI proof.</h1>
          <p class="hero-copy">Monitor or import reviews, classify sentiment and risk, draft editable responses, approve before publishing, and prove the hours and revenue protected by faster replies.</p>
          <div class="hero-actions">
            <button class="primary" data-action="seed-drafts">Generate safe drafts</button>
            <button class="secondary" data-tab="setup">Import / setup</button>
          </div>
        </div>
        <aside class="hero-card" aria-label="ROI snapshot">
          <span class="card-label">This month's control room</span>
          <strong>${metrics.unhandledReviews}</strong>
          <span>reviews still need action</span>
          <div class="mini-grid">
            <span><b>${metrics.highRiskReviews}</b> risky</span>
            <span><b>${pct(metrics.responseCoverage)}</b> covered</span>
            <span><b>${money(metrics.estimatedMonthlyLaborValue)}</b> labor value</span>
          </div>
        </aside>
      </section>
    </header>
    <main id="dashboard" class="layout">
      <section class="tabs" aria-label="Product sections">
        ${(['inbox', 'reports', 'billing', 'setup'] as Tab[])
          .map((tab) => `<button class="tab ${state.activeTab === tab ? 'active' : ''}" data-tab="${tab}">${tab}</button>`)
          .join('')}
      </section>
      ${state.toast ? `<div class="toast" role="status">${escapeHtml(state.toast)}</div>` : ''}
      ${content}
    </main>
  `;
}

function renderInbox(): string {
  const review = selectedReview();
  if (!review) return '<section class="panel">No reviews yet. Import a CSV or load the demo dataset.</section>';
  const analysis = classifyReview(review);
  return appShell(`
    <section class="workspace">
      <aside class="queue panel" aria-label="Review queue">
        <div class="section-title">
          <div>
            <p class="eyebrow">Unified inbox</p>
            <h2>${state.reviews.length} reviews</h2>
          </div>
          <button class="ghost" data-action="add-manual">+ Manual</button>
        </div>
        <div class="queue-list">
          ${state.reviews
            .map((item) => {
              const itemAnalysis = classifyReview(item);
              return `<button class="review-row ${item.id === review.id ? 'selected' : ''}" data-select-review="${escapeHtml(item.id)}">
                <span class="row-top"><b>${escapeHtml(item.reviewerName)}</b><span>${item.rating}★</span></span>
                <span>${escapeHtml(item.source)} · ${formatDate(item.createdAt)}</span>
                <span class="badges"><i class="badge ${itemAnalysis.risk}">${itemAnalysis.risk}</i><i class="badge">${statusLabel(item.status)}</i></span>
              </button>`;
            })
            .join('')}
        </div>
      </aside>

      <section class="review-detail panel">
        <div class="section-title">
          <div>
            <p class="eyebrow">${escapeHtml(review.source)} · ${escapeHtml(review.location)}</p>
            <h2>${escapeHtml(review.reviewerName)} · ${review.rating}★</h2>
          </div>
          <span class="risk-pill ${analysis.risk}">${analysis.urgency} urgency · ${analysis.risk} risk</span>
        </div>

        <blockquote>${escapeHtml(analysis.redactedComment)}</blockquote>

        <div class="insight-grid">
          <article><span>Sentiment</span><b>${analysis.sentiment}</b></article>
          <article><span>Risk score</span><b>${analysis.score}/100</b></article>
          <article><span>Tags</span><b>${escapeHtml(analysis.tags.join(', '))}</b></article>
        </div>

        <details open>
          <summary>Why this needs this workflow</summary>
          <ul>${analysis.reasons.map((reason) => `<li>${escapeHtml(reason)}</li>`).join('')}</ul>
        </details>

        <label class="draft-label" for="draft">Editable response draft</label>
        <textarea id="draft" data-draft-for="${escapeHtml(review.id)}" rows="8">${escapeHtml(review.responseDraft ?? buildDraftResponse(review, state.profile))}</textarea>

        <div class="button-row">
          <button class="secondary" data-action="regenerate" data-review-id="${escapeHtml(review.id)}">Regenerate safer draft</button>
          <button class="warning" data-action="escalate" data-review-id="${escapeHtml(review.id)}">Escalate</button>
          <button class="primary" data-action="approve" data-review-id="${escapeHtml(review.id)}">Approve</button>
          <button class="ghost" data-action="publish" data-review-id="${escapeHtml(review.id)}">Mark published</button>
        </div>
        <p class="compliance-note">Publishing is deliberately manual/API-gated. The app creates a reviewed response suggestion and never pretends the customer interaction happened.</p>
      </section>
    </section>
  `);
}

function renderReports(): string {
  const metrics = aggregateMetrics(state.reviews, state.profile);
  const risky = state.reviews.filter((review) => riskClass(review) === 'high');
  return appShell(`
    <section class="panel report">
      <div class="section-title">
        <div>
          <p class="eyebrow">Reporting and retention value</p>
          <h2>Weekly owner report</h2>
        </div>
        <button class="primary" data-action="export-report">Copy report</button>
      </div>
      <div class="metric-grid">
        <article><span>Coverage</span><b>${pct(metrics.responseCoverage)}</b><small>approved or published</small></article>
        <article><span>Average rating</span><b>${metrics.averageRating}★</b><small>current queue</small></article>
        <article><span>Open risk</span><b>${metrics.highRiskReviews}</b><small>human review required</small></article>
        <article><span>Labor value</span><b>${money(metrics.estimatedMonthlyLaborValue)}</b><small>${metrics.estimatedMonthlyHoursSaved}h/month saved</small></article>
      </div>
      <div class="two-col">
        <article class="card">
          <h3>ROI model</h3>
          <p>${escapeHtml(metrics.roiNarrative)}</p>
          <p><b>${money(metrics.estimatedRevenueAtRisk)}</b> estimated revenue at risk from unresolved critical reviews, using average ticket and current queue risk as a conservative prioritization signal.</p>
        </article>
        <article class="card">
          <h3>Risk queue</h3>
          ${risky.length ? `<ul>${risky.map((review) => `<li>${escapeHtml(review.source)} ${review.rating}★ · ${escapeHtml(sanitizeReviewText(review.comment))}</li>`).join('')}</ul>` : '<p>No high-risk reviews in the current queue.</p>'}
        </article>
      </div>
      <article class="card">
        <h3>Analytics events</h3>
        <div class="event-list">${state.eventLog
          .map((event) => `<p><b>${escapeHtml(event.event)}</b><span>${formatDate(event.at)}</span><small>${escapeHtml(event.detail)}</small></p>`)
          .join('')}</div>
      </article>
    </section>
  `);
}

function renderBilling(): string {
  return appShell(`
    <section class="panel billing">
      <div class="section-title">
        <div>
          <p class="eyebrow">Subscription wedge</p>
          <h2>Clear ROI, not enterprise bloat</h2>
        </div>
      </div>
      <div class="pricing-grid">
        ${pricingPlans
          .map(
            (plan) => `<article class="price-card ${state.profile.plan === plan.id ? 'current' : ''}">
              <span>${escapeHtml(plan.name)}</span>
              <h3>€${plan.price}<small>/mo</small></h3>
              <p>${escapeHtml(plan.subtitle)}</p>
              <ul>${plan.features.map((feature) => `<li>${escapeHtml(feature)}</li>`).join('')}</ul>
              <button class="primary" data-action="checkout" data-plan="${escapeHtml(plan.id)}">${escapeHtml(plan.cta)}</button>
            </article>`
          )
          .join('')}
      </div>
      <article class="card">
        <h3>Stripe test-mode design</h3>
        <p>The Pages Function <code>/api/checkout</code> expects Stripe test price IDs through environment variables and returns a checkout URL only when <code>STRIPE_SECRET_KEY</code> is configured. No secrets are committed.</p>
      </article>
    </section>
  `);
}

function renderSetup(): string {
  const csvExample = 'source,location,reviewer,rating,comment,createdAt\nGoogle,Madrid,Ana,2,Waited too long and nobody helped,2026-09-12T09:00:00.000Z';
  return appShell(`
    <section class="setup-grid">
      <form class="panel setup-form" data-form="profile">
        <p class="eyebrow">Onboarding</p>
        <h2>Business profile</h2>
        <label>Business name <input name="businessName" value="${escapeHtml(state.profile.businessName)}" /></label>
        <label>Industry <input name="industry" value="${escapeHtml(state.profile.industry)}" /></label>
        <label>Location <input name="location" value="${escapeHtml(state.profile.location)}" /></label>
        <label>Brand voice
          <select name="brandVoice">
            ${(['professional', 'warm', 'concise'] as BrandVoice[]).map((voice) => `<option value="${voice}" ${voice === state.profile.brandVoice ? 'selected' : ''}>${voice}</option>`).join('')}
          </select>
        </label>
        <label>Monthly reviews <input name="monthlyReviews" type="number" min="0" value="${state.profile.monthlyReviews}" /></label>
        <label>Average ticket (€) <input name="averageTicket" type="number" min="0" value="${state.profile.averageTicket}" /></label>
        <label>Staff cost/hour (€) <input name="laborCostPerHour" type="number" min="0" value="${state.profile.laborCostPerHour}" /></label>
        <button class="primary" type="submit">Save profile</button>
      </form>

      <section class="panel">
        <p class="eyebrow">Import without scraping</p>
        <h2>CSV / manual ingestion</h2>
        <p>Paste exported review data or use the demo. This avoids scraping and keeps API publishing behind explicit business authorization.</p>
        <textarea data-csv rows="8" aria-label="CSV import">${escapeHtml(csvExample)}</textarea>
        <div class="button-row">
          <button class="secondary" data-action="import-csv">Import CSV</button>
          <button class="ghost" data-action="reset-demo">Load demo data</button>
        </div>
        <h3>Market constraints baked into MVP</h3>
        <ul>${marketSignals.map((signal) => `<li>${escapeHtml(signal)}</li>`).join('')}</ul>
      </section>
    </section>
  `);
}

function render(): void {
  const root = document.querySelector<HTMLDivElement>('#app');
  if (!root) return;
  const view = {
    inbox: renderInbox,
    reports: renderReports,
    billing: renderBilling,
    setup: renderSetup
  }[state.activeTab]();
  root.innerHTML = view;
  bindEvents(root);
}

function bindEvents(root: HTMLDivElement): void {
  root.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((button) => {
    button.addEventListener('click', () => setState({ activeTab: button.dataset.tab as Tab }));
  });

  root.querySelectorAll<HTMLButtonElement>('[data-select-review]').forEach((button) => {
    button.addEventListener('click', () => setState({ selectedReviewId: button.dataset.selectReview ?? null }));
  });

  root.querySelectorAll<HTMLTextAreaElement>('[data-draft-for]').forEach((textarea) => {
    textarea.addEventListener('input', () => updateReview(textarea.dataset.draftFor ?? '', { responseDraft: textarea.value }));
  });

  root.querySelectorAll<HTMLButtonElement>('[data-action]').forEach((button) => {
    button.addEventListener('click', () => handleAction(button));
  });

  root.querySelector<HTMLFormElement>('[data-form="profile"]')?.addEventListener('submit', (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget as HTMLFormElement);
    const profile: BusinessProfile = {
      ...state.profile,
      businessName: String(form.get('businessName') ?? state.profile.businessName),
      industry: String(form.get('industry') ?? state.profile.industry),
      location: String(form.get('location') ?? state.profile.location),
      brandVoice: String(form.get('brandVoice') ?? state.profile.brandVoice) as BrandVoice,
      monthlyReviews: Number(form.get('monthlyReviews') ?? state.profile.monthlyReviews),
      averageTicket: Number(form.get('averageTicket') ?? state.profile.averageTicket),
      laborCostPerHour: Number(form.get('laborCostPerHour') ?? state.profile.laborCostPerHour)
    };
    const reviews = state.reviews.map((review): Review => ({ ...review, responseDraft: buildDraftResponse(review, profile) }));
    setState({ profile, reviews, toast: 'Profile saved and drafts regenerated.' }, { event: 'profile_saved', detail: profile.businessName });
  });
}

function handleAction(button: HTMLButtonElement): void {
  const action = button.dataset.action;
  const reviewId = button.dataset.reviewId;

  if (action === 'reset-demo') {
    state = defaultState;
    track('demo_reset', 'Demo dataset restored.');
    render();
    return;
  }

  if (action === 'seed-drafts') {
    const reviews = createDraftedReviews(state.reviews, state.profile);
    setState({ reviews, toast: 'Safe editable drafts generated for the queue.' }, { event: 'drafts_generated', detail: `${reviews.length} reviews processed` });
    return;
  }

  if (action === 'add-manual') {
    const review: Review = {
      id: `manual-${Date.now()}`,
      source: 'Manual',
      location: state.profile.location,
      reviewerName: 'New customer',
      rating: 3,
      comment: 'Paste the customer review text here, then regenerate a draft.',
      createdAt: new Date().toISOString(),
      status: 'new'
    };
    setState({ reviews: [review, ...state.reviews], selectedReviewId: review.id, toast: 'Manual review added.' }, { event: 'manual_review_added', detail: review.location });
    return;
  }

  if (action === 'regenerate' && reviewId) {
    const review = state.reviews.find((item) => item.id === reviewId);
    if (review) updateReview(reviewId, { responseDraft: buildDraftResponse(review, state.profile), status: initialReviewStatus(review) }, 'draft_regenerated');
    return;
  }

  if (action === 'approve' && reviewId) {
    updateReview(reviewId, { status: 'approved', approvedAt: new Date().toISOString() }, 'response_approved');
    return;
  }

  if (action === 'publish' && reviewId) {
    updateReview(reviewId, { status: 'published', publishedAt: new Date().toISOString() }, 'response_marked_published');
    return;
  }

  if (action === 'escalate' && reviewId) {
    updateReview(reviewId, { status: 'escalated' }, 'review_escalated');
    return;
  }

  if (action === 'import-csv') {
    const csv = document.querySelector<HTMLTextAreaElement>('[data-csv]')?.value ?? '';
    const imported = parseCsvReviews(csv).map((review): Review => ({
      ...review,
      responseDraft: buildDraftResponse(review, state.profile),
      status: initialReviewStatus(review)
    }));
    if (!imported.length) {
      setState({ toast: 'No CSV rows imported. Keep the header row and at least one review.' });
      return;
    }
    setState({ reviews: [...imported, ...state.reviews], selectedReviewId: imported[0].id, activeTab: 'inbox', toast: `${imported.length} review imported.` }, { event: 'csv_imported', detail: `${imported.length} rows` });
    return;
  }

  if (action === 'export-report') {
    void navigator.clipboard?.writeText(serializeReport(state.reviews, state.profile));
    setState({ toast: 'Report copied to clipboard.' }, { event: 'report_exported', detail: state.profile.businessName });
    return;
  }

  if (action === 'checkout') {
    const plan = button.dataset.plan ?? 'starter';
    void requestCheckout(plan);
  }
}

async function requestCheckout(plan: string): Promise<void> {
  try {
    const response = await fetch('/api/checkout', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ plan })
    });
    const body = (await response.json()) as { url?: string; message?: string; mode?: string };
    if (body.url) {
      track('checkout_created', `${plan} Stripe test checkout`);
      window.location.href = body.url;
      return;
    }
    setState({ toast: body.message ?? 'Stripe test checkout is not configured in this environment.' }, { event: 'checkout_preview', detail: `${plan} plan selected` });
  } catch {
    setState({ toast: 'Checkout endpoint unavailable locally. Configure Stripe env vars in Cloudflare Pages later.' }, { event: 'checkout_preview', detail: `${plan} plan selected` });
  }
}

render();
